import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, FormGroup, Label } from 'reactstrap';
import { formatPhoneNumber } from '../../../utilities/Utils';
import '../../../styles/WarmLeadPopup.css'; 
import { getMyUser } from '../../../api/User'; 
import { addWarmLeads } from '../../../api/Company'; // Import your API function
import { WarmLeads } from '../../../api/Company'; // Import the WarmLeads interface

interface UserDetails {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

interface AddPropertyPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (properties: string, notes: string) => void; 
}

const AddPropertyPopup: React.FC<AddPropertyPopupProps> = ({ isOpen, onClose, onSubmit }) => {
  const [properties, setProperties] = useState<Array<{ name: string; unitCount: number }>>([]);
  const [propertyName, setPropertyName] = useState('');
  const [unitCount, setUnitCount] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [unitCountError, setUnitCountError] = useState<string | null>(null);
  const [formattedProperties, setFormattedProperties] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userResult = await getMyUser();

        if (userResult.success) {
          setUserDetails({
            firstName: userResult.data!.firstName,
            lastName: userResult.data!.lastName,
            phoneNumber: formatPhoneNumber(userResult.data!.phone),
            email: userResult.data!.email,
          });
        } else {
          setErrorMessage(userResult.error!);
        }
      } catch (error) {
        console.error('Failed to fetch user details', error);
        setErrorMessage('Failed to fetch user details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const formatProperties = () => {
      return properties.map(p => `${p.name} - ${p.unitCount} units`).join(', ');
    };
    setFormattedProperties(formatProperties());
  }, [properties]);

  const handleAddProperty = () => {
    if (!propertyName || unitCount.trim() === '') {
      setSubmitError('Property Name and Unit Count are required.');
      return;
    }

    const unitCountValue = Number(unitCount);
    if (unitCountValue <= 0) {
      setUnitCountError('Unit Count must be greater than zero.');
      return;
    } else {
      setUnitCountError(null);
    }

    const isDuplicate = properties.some(p => p.name.toLowerCase() === propertyName.toLowerCase());

    if (isDuplicate) {
      setDuplicateError('Property Name already exists.');
      return;
    }

    setProperties(prevProperties => [
      ...prevProperties,
      { name: propertyName, unitCount: unitCountValue }
    ]);
    setPropertyName('');
    setUnitCount('');
    setSubmitError(null);
    setDuplicateError(null);
  };

  const handleRemoveProperty = (index: number) => {
    setProperties(prevProperties => 
      prevProperties.filter((_, i) => i !== index)
    );
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (properties.length === 0) {
      setSubmitError('Please add at least one property.');
      return;
    }

    try {
      const warmLeads: WarmLeads = {
        properties: formattedProperties,
        notes
      };

      const response = await addWarmLeads(warmLeads);
      
      if (response.success) {
        onSubmit(formattedProperties, notes);
        onClose(); 
      } else {
        setSubmitError('Failed to add properties. Please try again.');
      }
    } catch (error) {
      console.error('Failed to submit properties', error);
      setSubmitError('An error occurred while submitting properties.');
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={handleCancel} className="property-popup">
      <ModalHeader toggle={handleCancel}>Add Properties</ModalHeader>
      <ModalBody>
        {isLoading ? (
          <p>Loading user details...</p>
        ) : errorMessage ? (
          <p className="text-danger">Error: {errorMessage}</p>
        ) : userDetails ? (
          <div className="container">
            <div className="heading">User Information</div>
            <div className="info-grid">
              <div className="info-row">
                <div className="info-item">
                  <span className="label">First Name:</span>
                  <span className="value">{userDetails.firstName}</span>
                </div>
                <div className="info-item">
                  <span className="label">Last Name:</span>
                  <span className="value">{userDetails.lastName}</span>
                </div>
              </div>
              <div className="info-row">
                <div className="info-item">
                  <span className="label">Phone Number:</span>
                  <span className="value">{userDetails.phoneNumber}</span>
                </div>
                <div className="info-item">
                  <span className="label">Email:</span>
                  <span className="value">{userDetails.email}</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <p>No user details available.</p>
        )}
        <div className="heading">Property Information</div>
        <div className="mt-3">
          <Input
            type="textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Enter your notes here..."
            className="notes-textarea mt-4"
          />
          <div className="property-inputs">
            <div className="property-row d-flex align-items-center">
              <FormGroup className="flex-grow-1 mr-2" style={{ flexBasis: '80%' }}>
                <Label for="propertyName">Property Name</Label>
                <Input
                  id="propertyName"
                  type="text"
                  value={propertyName}
                  onChange={(e) => setPropertyName(e.target.value)}
                  className="property-input"
                />
              </FormGroup>
              <FormGroup className="mr-2" style={{ flexBasis: '10%' }}>
                <Label for="unitCount">Units</Label>
                <Input
                  id="unitCount"
                  type="number"
                  value={unitCount}
                  onChange={(e) => setUnitCount(e.target.value)}
                  className="unit-input"
                />
              </FormGroup>
              <Button color="primary" onClick={handleAddProperty} style={{ flexBasis: '10%' }} className="add-property-btn">+</Button>
            </div>
            {submitError && <p className="text-danger mt-2">{submitError}</p>}
            {duplicateError && <p className="text-danger mt-2">{duplicateError}</p>}
            {unitCountError && <p className="text-danger mt-2">{unitCountError}</p>}
          </div>
        </div>
        <div className="mt-3 property-list">
          <div className="property-summary">
            <strong>Properties:</strong> {formattedProperties}
          </div>
          {properties.map((property, index) => (
            <div key={index} className="property-item">
              <span>{property.name} - {property.unitCount} units</span>
              <Button color="danger" onClick={() => handleRemoveProperty(index)} className="remove-property-btn">-</Button>
            </div>
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleCancel}>Cancel</Button>
        <Button color="primary" onClick={handleSubmit}>Submit</Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddPropertyPopup;


// WEBPACK FOOTER //
// ./src/components/pages/admin/AddPropertyPopup.tsx